import constants from "../../../../../../../../appConstants";
import {
  DropdownEditor,
  NumericEditor,
  TextFieldEditor,
  SearchEditor,
} from "../../../../../../../dataGrid/cellEditors";
import { DropdownValueCellRenderer } from "../../../../../../../dataGrid/cellRenderer";
import { MultiSelectFilter } from "../../../../../../../dataGrid/columnFilters";
import { suppressEnter } from "../../../../../../../dataGrid/gridUtilities";
import { getFieldError, isEmptyShipmentObj, fetchLocationData } from "../utils";
import { isVoidPickupMode } from "../../utils";
import LocationDropdownItem from "./components/LocationDropdownItem";
import { FORM_VALIDATION_MSG, PICKUP_MANAGEMENT_MODES } from "../../constants";

export default class GridConfig {
  constructor(configuration, title) {
    const config = configuration || {};
    this.name = "GridConfiguration";
    this.config = GridConfig.getConfiguration(config, title);
  }
  static getConfiguration(userConfig, title) {
    return GridConfig.getDefaultConfiguration(title, userConfig);
  }

  static setPriorityShipment(params) {
    if (params.data.priority === params.newValue?.value) {
      return false;
    }
    params.data.priority = params.newValue?.value ?? null;
    return true;
  }

  static getPriorityShipment(params) {
    if (!params.data.priority) {
      return "None";
    }
    return params.data.priority;
  }

  static setPackageCode(params) {
    if (params.data.packageCode === params.newValue?.value) {
      return false;
    }
    params.data.packageCode = params.newValue?.value ?? null;
    return true;
  }

  static isFieldEditable(params) {
    return !isVoidPickupMode(params.api.mode);
  }

  static fieldValidation(params) {
    if (!params.api.isDataUpdatedOnGrid || isEmptyShipmentObj(params.data)) {
      return false;
    }
    return Boolean(
      getFieldError({
        field: params.colDef.field,
        value: params.value,
        mode: params.api.mode,
      })
    );
  }

  static setZipCode = (params) => {
    if (
      params.data.destinationZip === params.newValue?.value ||
      !params.newValue
    ) {
      return false;
    }
    params.data.destinationZip = params.newValue ?? params.newValue.zip;
    return true;
  };

  static zipDropdownValueCellRenderer = (params) => {
    return params.value && params.value.zip !== undefined
      ? params.value.zip
      : params.value;
  };

  static getPackageCode = (params) => {
    return params.data.packageCode;
  }

  static suppressTabOnLoading = (gridApi) => {
    if (
      [
        PICKUP_MANAGEMENT_MODES.MERGE_PICKUP,
        PICKUP_MANAGEMENT_MODES.EDIT_PICKUP,
      ].includes(gridApi.mode)
    ) {
      return false;
    }
    return !isEmptyShipmentObj(gridApi.getModel().rowsToDisplay.at(-1).data)
  }

  static setDefaultValueZero = (params) => {
    const { field } = params.colDef;
    const valueChanged = params.data[field] !== params.newValue;
    if (valueChanged) {
      params.data[field] = params.newValue || 0;
    }
    return valueChanged;
  }

  static getDefaultConfiguration(title, userConfig) {
    return {
      title: title,
      keyColumn: "",
      rowSelection: "multiple",
      rowDeselection: true,
      defaultColDef: {
        cellEditorFramework: TextFieldEditor,
        lockPinned: true,
        sortable: true,
        filter: true,
        resizable: true,
        editable: GridConfig.isFieldEditable,
        lockPosition: true,
      },
      columns: [
        {
          headerName: "HU (Opt)",
          headerTooltip: "Handling Unit (Optional)",
          field: "handlingUnits",
          minWidth: constants.columnWidth.HU,
          cellClass: "gridCell-centerAlign",
          cellEditorFramework: NumericEditor,
          filterFramework: MultiSelectFilter,
          cellClassRules: {
            cellError: GridConfig.fieldValidation,
          },
          valueSetter : GridConfig.setDefaultValueZero
        },
        {
          headerName: "Pieces (Opt)",
          field: "pieces",
          headerTooltip: "Pieces (Optional)",
          minWidth: constants.columnWidth.PIECES,
          cellClass: "gridCell-centerAlign",
          cellEditorFramework: NumericEditor,
          filterFramework: MultiSelectFilter,
          valueSetter : GridConfig.setDefaultValueZero
        },
        {
          headerName: "Pkg Type",
          headerTooltip: "Package Type",
          field: "packageCodeWithSuffix",
          valueGetter: GridConfig.getPackageCode,
          minWidth: constants.columnWidth.PKG_TYPE,
          cellClass: "gridCell-centerAlign",
          filterFramework: MultiSelectFilter,
          cellEditorFramework: DropdownEditor,
          cellRenderer: DropdownValueCellRenderer,
          suppressKeyboardEvent: suppressEnter,
          cellEditorParams: {
            isClearable: true,
            openMenuOnFocus: true,
          },
          cellClassRules: {
            cellError: GridConfig.fieldValidation,
          },
          valueSetter: GridConfig.setPackageCode,
        },
        {
          headerName: "Total Weight",
          field: "weight",
          minWidth: constants.columnWidth.WEIGHT,
          cellClass: "gridCell-centerAlign",
          cellEditorFramework: NumericEditor,
          filterFramework: MultiSelectFilter,
          cellClassRules: {
            cellError: GridConfig.fieldValidation,
          },
          valueSetter : GridConfig.setDefaultValueZero
        },
        {
          headerName: "Dest Zip",
          headerTooltip: "Destination Zip",
          field: "destinationZip",
          minWidth: constants.columnWidth.ZIP_CODE,
          cellClass: "gridCell-leftAlign",
          cellEditorFramework: SearchEditor,
          suppressKeyboardEvent: suppressEnter,
          cellRenderer: GridConfig.zipDropdownValueCellRenderer,
          cellEditorParams: {
            openMenuOnFocus: true,
            placeholder: "Enter Zip",
            fetchData:  fetchLocationData,
            fieldName: "zip",
            OptionRenderer: LocationDropdownItem,
            suppressTabOnLoading: GridConfig.suppressTabOnLoading,
            errorMessage: FORM_VALIDATION_MSG.ZIP_CODE.INVALID_VALUE,
            terminalCode: userConfig.terminal.code
          },
          filterFramework: MultiSelectFilter,
          cellClassRules: {
            cellError: GridConfig.fieldValidation,
          },
          valueSetter: GridConfig.setZipCode,
        },
        {
          headerName: "Dest City",
          headerTooltip: "Destination City",
          field: "destinationCity",
          minWidth: constants.columnWidth.WEIGHT,
          cellClass: "gridCell-centerAlign",
          filterFramework: MultiSelectFilter,
          cellClassRules: {
            cellError: GridConfig.fieldValidation,
          },
          editable: false,
        },
        {
          headerName: "Dest Term",
          headerTooltip: "Destination Terminal",
          field: "destinationTerminal",
          minWidth: constants.columnWidth.TERMINAL,
          cellClass: "gridCell-centerAlign",
          filterFramework: MultiSelectFilter,
          cellClassRules: {
            cellError: GridConfig.fieldValidation,
          },
          editable: false,
        },
        {
          headerName: "Service Level",
          field: "serviceLevel",
          minWidth: constants.columnWidth.SERVICE_LEVEL,
          cellClass: "gridCell-centerAlign",
          filterFramework: MultiSelectFilter,
          cellEditorFramework: DropdownEditor,
          cellRenderer: DropdownValueCellRenderer,
          suppressKeyboardEvent: suppressEnter,
          cellEditorParams: {
            openMenuOnFocus: true,
            isClearable: true,
          },
          valueSetter: GridConfig.setPriorityShipment,
          valueGetter: GridConfig.getPriorityShipment,
        },
      ],
    };
  }
}
